import { Link } from "react-router-dom";
import { Check, Crown, Gem, Star, ArrowUpRight } from "lucide-react";
import { ArrowLeft } from "lucide-react";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";

const TIERS = [
  {
    id: "gold",
    name: "Gold",
    icon: Star,
    price: "₹ 5 Lac",
    priceNote: "+ taxes",
    slots: "3 slots",
    color: "#C7A24A",
    badge: "Essential visibility",
    deliverables: [
      "Logo placement on the BoT Summit website (Gold tier section)",
      "Logo on on-stage sponsor backdrop and event loop reel",
      "2 complimentary delegate passes for both days",
      "3 × 3 m exhibitor table in the main expo area",
      "1 dedicated social-media shout-out (LinkedIn + X) pre-event",
      "Inclusion of brand in the official delegate welcome e-mail",
      "Logo in the post-event digital report shared with 500+ delegates",
      "Access to the sponsor lounge during both days",
    ],
  },
  {
    id: "platinum",
    name: "Platinum",
    icon: Crown,
    price: "₹ 7.5 Lac",
    priceNote: "+ taxes",
    slots: "2 slots",
    color: "#6E85FF",
    badge: "Premium presence",
    highlighted: true,
    deliverables: [
      "Everything included in the Gold tier, plus:",
      "Priority logo placement on website, backdrop and loop reel",
      "4 complimentary delegate passes + 1 Gala Dinner invite",
      "3 × 3 m premium exhibitor booth in a high-traffic zone",
      "15-minute speaking slot in a curated panel or fireside",
      "3 × dedicated social-media shout-outs across LinkedIn and X",
      "Logo on printed delegate badge lanyards",
      "Lead data from opt-in attendees who visit your booth",
      "Co-branding on one themed networking session (e.g. coffee break)",
      "Dedicated account manager from the BoT Summit secretariat",
    ],
  },
  {
    id: "diamond",
    name: "Diamond",
    icon: Gem,
    price: "₹ 12 Lac",
    priceNote: "+ taxes",
    slots: "1 slot",
    color: "#002FA7",
    badge: "Title co-presentation",
    deliverables: [
      "Everything included in the Platinum tier, plus:",
      "Title co-presentation — “BoT Summit 2026, Powered by [Your Brand]”",
      "Top logo placement on every on-stage backdrop, loop reel and website hero",
      "8 complimentary delegate passes + 2 VIP Gala Dinner invites",
      "30-minute keynote slot on the main stage",
      "One branded breakout session or workshop (45 minutes, self-curated)",
      "3 × 3 m premium exhibitor booth with priority location",
      "Branded gift / collateral inside every delegate welcome kit",
      "Inclusion in the official press release and media announcements",
      "Exclusive 1:1 roundtable with 10 pre-qualified CIO / CTO delegates",
      "Year-round brand presence on the BoT Summit website until Summit 2027",
    ],
  },
];

export default function Sponsorship() {
  return (
    <div className="min-h-screen bg-white text-[#111]">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="sponsor-home-link" className="flex items-center gap-3">
            <span className="w-9 h-9 bg-[#002FA7] text-white font-display text-sm flex items-center justify-center">
              BoT
            </span>
            <div className="leading-none hidden sm:block">
              <div className="font-display text-base tracking-tight">BoT Summit 2026</div>
              <div className="overline text-[10px] text-zinc-500 mt-0.5">Best of Tech</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="sponsor-back-btn"
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase border border-zinc-300 px-4 py-2 hover:bg-black hover:text-white hover:border-black transition-colors"
            >
              <ArrowLeft size={14} /> Back
            </Link>
            <a
              href="mailto:info@botsummits.com?subject=BoT%20Summit%202026%20Sponsorship%20Enquiry"
              data-testid="sponsor-contact-btn"
              className="hidden md:inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2.5 text-[12px] font-bold tracking-widest uppercase hover:bg-black transition-colors"
            >
              Enquire →
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="overline text-[#FF2A00] mb-6">/ Sponsorship · BoT Summit 2026</div>
          <div className="grid grid-cols-12 gap-y-8 md:gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9]">
                Put your brand on
                <br />
                the <span className="text-[#002FA7]">Best of Tech.</span>
              </h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-zinc-700 text-base leading-relaxed">
                BoT Summit 2026 brings together 500+ CIOs, CTOs, founders and policymakers
                across Europe, Asia and India. Our sponsorship tiers are designed to put your
                brand in the room where the decisions are made.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-zinc-500">/ 03 tiers</div>
              <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-2">
                Choose your partnership tier.
              </h2>
            </div>
            <div className="hidden md:block overline text-zinc-400 text-right">
              Limited slots — first confirmed first served.
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-l border-zinc-200">
            {TIERS.map((t, i) => {
              const Icon = t.icon;
              return (
                <article
                  key={t.id}
                  data-testid={`tier-${t.id}`}
                  className={`border-r border-b border-zinc-200 p-8 md:p-10 relative flex flex-col ${
                    t.highlighted ? "bg-[#0A0A0A] text-white" : "bg-white"
                  }`}
                >
                  {t.highlighted && (
                    <div className="absolute top-0 right-0 bg-[#FF2A00] text-white px-3 py-1 overline text-[10px]">
                      Most chosen
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <Icon size={32} style={{ color: t.highlighted ? "#6E85FF" : t.color }} strokeWidth={1.5} />
                    <span className={`overline ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                      0{i + 1} / 03
                    </span>
                  </div>
                  <h3 className="font-display text-4xl uppercase tracking-tighter mt-8">
                    {t.name}
                  </h3>
                  <div className={`overline mt-2 ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                    {t.badge}
                  </div>

                  <div className={`overline mt-8 ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                    {t.slots}
                  </div>

                  <div className={`mt-8 pt-8 border-t ${t.highlighted ? "border-zinc-800" : "border-zinc-200"} flex-1`}>
                    <div className={`overline mb-5 ${t.highlighted ? "text-zinc-400" : "text-zinc-500"}`}>
                      Deliverables
                    </div>
                    <ul className="space-y-3">
                      {t.deliverables.map((d, k) => (
                        <li key={k} className="flex gap-3 text-sm leading-relaxed">
                          <Check
                            size={16}
                            className="shrink-0 mt-0.5"
                            style={{ color: t.highlighted ? "#6E85FF" : t.color }}
                          />
                          <span className={t.highlighted ? "text-zinc-200" : "text-zinc-700"}>
                            {d}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a
                    href={`mailto:info@botsummits.com?subject=BoT%20Summit%202026%20${t.name}%20Sponsorship`}
                    data-testid={`tier-cta-${t.id}`}
                    className={`mt-10 inline-flex items-center justify-center gap-2 w-full px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase transition-colors ${
                      t.highlighted
                        ? "bg-white text-black hover:bg-[#FF2A00] hover:text-white"
                        : "bg-[#002FA7] text-white hover:bg-black"
                    }`}
                  >
                    Enquire about {t.name} <ArrowUpRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison / extras */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-12 gap-y-8 md:gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="overline text-[#FF2A00]">/ Custom</div>
              <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-3">
                Need something bespoke?
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7">
              <p className="text-zinc-700 leading-relaxed">
                Category exclusivity, themed lounges, custom activations, sponsored dinners,
                branded transport, delegate-swag take-overs — we regularly build bespoke
                partnerships on top of the tiers above. Reach out and we'll put together a
                proposal tailored to your goals.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="mailto:info@botsummits.com?subject=Custom%20Sponsorship%20-%20BoT%20Summit%202026"
                  data-testid="custom-sponsor-cta"
                  className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black transition-colors"
                >
                  info@botsummits.com
                </a>
                <a
                  href="https://wa.me/447915361665"
                  data-testid="sponsorship-whatsapp-uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors"
                >
                  <WhatsAppIcon size={14} /> +44 7915361665
                </a>
                <a
                  href="https://wa.me/918825035701"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors"
                >
                  <WhatsAppIcon size={14} /> +91 8825035701
                </a>
                <a
                  href="https://wa.me/971525330661"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sponsor-whatsapp-uae"
                  className="inline-flex items-center gap-2 border border-black text-black px-7 py-4 text-xs font-bold tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors"
                >
                  <WhatsAppIcon size={14} /> +971 525330661
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="overline text-zinc-500">
            © 2026 BoT Summit · Best of Tech
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 overline text-zinc-500">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/payment-terms" className="hover:text-white">Payment Terms</Link>
            <Link to="/code-of-conduct" className="hover:text-white">Code of Conduct</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
